import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtDate } from "@/lib/format";
import { euro, download } from "@/lib/accounting";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaymentDialog } from "@/components/accounting/PaymentDialog";

const STATES = { open: "Aperte (anche scadute)", overdue: "Solo scadute", paid: "Saldate", all: "Tutte" };

export const ScheduleTab = () => {
  const { can } = useAuth();
  const { companyId, nameOf } = useData();
  const [direction, setDirection] = useState("OUT");
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const load = useCallback(() => api.get("/accounting/schedule", { params: { direction, status, company_id: companyId || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [direction, status, companyId]);
  useEffect(() => { load(); }, [load]);
  const overdue = rows.filter((r) => r.state === "overdue");
  return (
    <div data-testid="schedule-tab">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex border bg-white">
          {[["OUT", "Incassi da clienti"], ["IN", "Pagamenti a fornitori"]].map(([k, l]) => (
            <button key={k} className={`px-4 py-2 text-sm ${direction === k ? "bg-primary font-semibold text-primary-foreground" : ""}`} onClick={() => setDirection(k)} data-testid={`schedule-dir-${k}`}>{l}</button>
          ))}
        </div>
        <select className="h-10 border bg-white px-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="schedule-status">{Object.entries(STATES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <span className="text-sm">Residuo <strong className="font-mono" data-testid="schedule-total">{euro(rows.reduce((s, r) => s + r.residual, 0))}</strong> · scaduto <strong className="font-mono text-red-700" data-testid="schedule-overdue">{euro(overdue.reduce((s, r) => s + r.residual, 0))}</strong> ({overdue.length})</span>
      </div>
      {rows.length === 0 ? <Empty text="Nessuna scadenza" testId="schedule-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Scadenza</TableHead><TableHead>Controparte</TableHead><TableHead>Fattura</TableHead><TableHead>Società</TableHead><TableHead className="text-right">Importo</TableHead><TableHead className="text-right">Residuo</TableHead><TableHead>Stato</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.installment_id} data-testid={`schedule-row-${r.installment_id}`}>
                  <TableCell className={`font-mono text-xs ${r.state === "overdue" ? "font-bold text-red-700" : ""}`}>{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-sm">{r.party_name}</TableCell>
                  <TableCell className="font-mono text-xs">{r.number} del {fmtDate(r.invoice_date)}</TableCell>
                  <TableCell className="text-xs">{nameOf("companies", r.company_id, "code")}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{euro(r.amount)}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">{euro(r.residual)}</TableCell>
                  <TableCell className="text-xs">{r.state === "overdue" ? <span className="font-semibold text-red-700">Scaduta da {r.days} gg</span> : r.state === "paid" ? "Saldata" : "In scadenza"}</TableCell>
                  <TableCell className="whitespace-nowrap">{can("accounting:write") && r.residual > 0 && <>
                    {direction === "OUT" && r.state === "overdue" && <Button size="sm" variant="ghost" className="mr-1" onClick={() => api.post(`/accounting/invoices/${r.invoice_id}/reminder`).then(() => toast.success("Sollecito inviato")).catch((e) => toast.error(errMsg(e)))} data-testid={`schedule-remind-${r.installment_id}`}>Sollecita</Button>}
                    <Button size="sm" variant="outline" onClick={() => setPayFor({ inv: { id: r.invoice_id, number: r.number, party_name: r.party_name, direction, payment_method: "MP05" }, inst: { id: r.installment_id, amount: r.amount, paid: r.paid } })} data-testid={`schedule-pay-${r.installment_id}`}>{direction === "OUT" ? "Incassa" : "Paga"}</Button></>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <PaymentDialog target={payFor} onClose={() => setPayFor(null)} onDone={load} />
    </div>
  );
};

export const FirstNoteTab = () => {
  const { companyId } = useData();
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const params = { date_from: from, date_to: to, company_id: companyId || undefined };
  useEffect(() => {
    let alive = true;
    api.get("/accounting/first-note", { params: { date_from: from, date_to: to, company_id: companyId || undefined } }).then((r) => alive && setData(r.data)).catch((e) => toast.error(errMsg(e)));
    return () => { alive = false; };
  }, [from, to, companyId]);
  return (
    <div data-testid="first-note-tab">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input type="date" className="w-44 bg-white" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="first-note-from" />
        <Input type="date" className="w-44 bg-white" value={to} onChange={(e) => setTo(e.target.value)} data-testid="first-note-to" />
        <span className="text-sm text-muted-foreground">{data?.rows.length || 0} movimenti (fatture emesse, ricevute, incassi e pagamenti)</span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => download("/accounting/first-note", { ...params, format: "xlsx" }, `prima-nota_${from}_${to}.xlsx`)} data-testid="first-note-xlsx">Scarica Excel</Button>
          <Button variant="outline" onClick={() => download("/accounting/first-note", { ...params, format: "csv" }, `prima-nota_${from}_${to}.csv`)} data-testid="first-note-csv">Scarica CSV</Button>
        </div>
      </div>
      {!data || data.rows.length === 0 ? <Empty text="Nessun movimento nel periodo" testId="first-note-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">{data.columns.map((c) => <TableHead key={c} className="whitespace-nowrap text-xs">{c}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{data.rows.map((r, i) => <TableRow key={i}>{r.map((v, j) => <TableCell key={j} className={`whitespace-nowrap text-xs ${typeof v === "number" ? "text-right font-mono" : ""}`}>{typeof v === "number" ? euro(v) : v}</TableCell>)}</TableRow>)}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
